import type { DataSource } from '../../types/siteRag';
import { buildUserKeyHeader, getCurrentUserApiKeys } from '../../lib/userKeyHeader';
import { authedFetch } from '../../lib/authedFetch';
import { prepareNrcsSdaRequest, fetchNrcsSdaRequest } from '../../lib/providers/nrcsSda';

/** Result from any data provider. */
export interface ProviderResult<T> {
  data: T;
  sources: DataSource[];
  /**
   * Optional live-result cache override. `0` means do not persist this result.
   * Providers use this when a successful degraded fallback depends on a
   * transiently unavailable applicability/identity check and must therefore be
   * retried on the next run instead of looking authoritative for the provider's
   * normal TTL. Omit for the provider's declared `cacheTtlMs`.
   */
  cacheTtlMs?: number;
}

/**
 * Coordinate context handed to every provider's `fetch`.
 *
 * `(lat, lng)` (the first two `fetch` args) is the coordinate the provider
 * SHOULD query at — the orchestrator resolves it per-provider via
 * `effectiveQueryCoord` (`index.ts`): the boundary centroid for
 * spatially-varying providers, the geocoded address for everything else and
 * for legacy sites with no drawn boundary.
 *
 * `geo` carries the *other* coordinates so a provider that needs more than a
 * single point — the FEMA multi-point flood sweep, the USFWS buffered-envelope
 * species query (CA4) — can reach the boundary polygon and the address
 * fallback. Optional so isolated tests / Node scripts can call `fetch(lat,
 * lng)` exactly as before.
 */
export interface ProviderGeoContext {
  studyAreaScope?: import('../../types/siteStudyArea').SiteStudyAreaScope;
  /** Attempt-owned cancellation/watchdog signal. */
  signal?: AbortSignal;
  /** The boundary centroid — `centroidOf(siteBoundary)`. When no boundary is
   *  drawn this equals `address` (the caller fills it in). */
  centroid: { lat: number; lng: number };
  /** The drawn boundary polygon, when one exists. ≥3 points. Used by the
   *  spatially-aware providers (FEMA flood sweep, USFWS envelope). */
  boundary?: { lat: number; lng: number }[];
  /** Canonical multi-parcel polygons. Providers must evaluate parcels
   * independently rather than drawing synthetic geometry between them. */
  boundaries?: { id: string; polygon: { lat: number; lng: number }[]; holes?: { lat: number; lng: number }[][] }[];
  /** The geocoded-address point — always present; the universal fallback. */
  address: { lat: number; lng: number };
  /** PH-A1.7 — Optional report-type hint for providers whose output
   *  framing depends on the downstream report. Currently consumed by
   *  `siteResearchProvider` to parametrize the "nuclear power + data
   *  center" framing — environmental / water-resource / test-fit
   *  reports get appropriate framing instead of the legacy hardcode.
   *  Undefined means "no specific report" — provider falls back to its
   *  default feasibility-style framing. */
  reportType?: string;
}

/** Inputs available when a provider needs a cache key more specific than the
 * default rounded point. Boundary-sensitive providers (FEMA, parcel zoning,
 * terrain) must include the polygon in their key so two parcels sharing a
 * centroid cannot reuse each other's result. */
export interface ProviderCacheKeyContext {
  lat: number;
  lng: number;
  geo?: ProviderGeoContext;
}

/** Every provider implements this interface. */
export interface DataProvider<T> {
  readonly id: string;
  readonly name: string;
  readonly cacheTtlMs: number;
  readonly requiresKey: boolean;
  /** Optional provider-owned cache-key suffix. Must be Firestore doc-id safe.
   * The cache layer prefixes this with `${id}:`. */
  cacheKey?(context: ProviderCacheKeyContext): string;
  fetch(lat: number, lng: number, apiKey?: string, geo?: ProviderGeoContext): Promise<ProviderResult<T>>;
}

/** Grid cell key for caching. Rounds to 2 decimals (~1.1km). */
export function gridCellKey(lat: number, lng: number): string {
  return `${lat.toFixed(2)}_${lng.toFixed(2)}`;
}

/**
 * Stable, rotation/winding-independent fingerprint of a parcel ring.
 * Coordinates are rounded to 5 decimals (~1.1 m latitude) before hashing so
 * harmless drawing precision jitter does not defeat the cache, while materially
 * different parcel boundaries still produce different keys.
 */
export function boundaryFingerprint(
  boundary: ReadonlyArray<{ lat: number; lng: number }> | undefined,
): string | null {
  if (!boundary) return null;
  const points = boundary
    .filter(p => Number.isFinite(p?.lat) && Number.isFinite(p?.lng))
    .map(p => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`);
  if (points.length < 3) return null;
  if (points.length > 1 && points[0] === points[points.length - 1]) points.pop();

  const canonicalRotation = (values: string[]): string => {
    let best = '';
    for (let i = 0; i < values.length; i++) {
      const candidate = [...values.slice(i), ...values.slice(0, i)].join(';');
      if (best === '' || candidate < best) best = candidate;
    }
    return best;
  };
  const forward = canonicalRotation(points);
  const reverse = canonicalRotation(points.slice().reverse());
  const canonical = forward < reverse ? forward : reverse;

  // FNV-1a, 64 bit. This is a cache partition rather than a security digest,
  // but 64 bits avoids the material birthday-collision risk of a 32-bit hash
  // once the shared provider cache contains many thousands of parcels.
  let hash = 0xcbf29ce484222325n;
  for (let i = 0; i < canonical.length; i++) {
    hash ^= BigInt(canonical.charCodeAt(i));
    hash = BigInt.asUintN(64, hash * 0x100000001b3n);
  }
  return hash.toString(16).padStart(16, '0');
}

/**
 * Stable, order/rotation/winding-independent fingerprint of a multi-polygon
 * or holed study area. Delegates to `boundaryFingerprint` for a single ring
 * without holes so existing cache keys remain bit-identical.
 */
export function multiBoundaryFingerprint(
  boundaries:
    | ReadonlyArray<{
        id?: string;
        polygon: ReadonlyArray<{ lat: number; lng: number }>;
        holes?: ReadonlyArray<ReadonlyArray<{ lat: number; lng: number }>>;
      }>
    | undefined,
): string | null {
  if (!boundaries || boundaries.length === 0) return null;
  if (boundaries.length === 1 && (!boundaries[0]!.holes || boundaries[0]!.holes.length === 0)) {
    return boundaryFingerprint(boundaries[0]!.polygon);
  }
  const partHashes: string[] = [];
  for (const parcel of boundaries) {
    const outerFp = boundaryFingerprint(parcel.polygon);
    if (!outerFp) return null;
    const holeFps: string[] = [];
    for (const hole of parcel.holes ?? []) {
      const hfp = boundaryFingerprint(hole);
      if (!hfp) return null;
      holeFps.push(hfp);
    }
    holeFps.sort();
    partHashes.push(holeFps.length > 0 ? `${outerFp}:${holeFps.join(',')}` : outerFp);
  }
  partHashes.sort();
  const combined = partHashes.join('|');
  let hash = 0xcbf29ce484222325n;
  for (let i = 0; i < combined.length; i++) {
    hash ^= BigInt(combined.charCodeAt(i));
    hash = BigInt.asUintN(64, hash * 0x100000001b3n);
  }
  return hash.toString(16).padStart(16, '0');
}

/** Haversine distance between two points in km. */
export function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** TTL constants */
export const CACHE_TTL = {
  CLIMATE_NORMALS: 30 * 24 * 60 * 60 * 1000,
  AIR_QUALITY:      1 * 24 * 60 * 60 * 1000,
  STANDARD:         7 * 24 * 60 * 60 * 1000,
} as const;

/** Validate lat/lng are within valid ranges. Throws if invalid. */
export function validateCoords(lat: number, lng: number): void {
  if (!isFinite(lat) || lat < -90 || lat > 90) throw new Error(`Invalid latitude: ${lat}`);
  if (!isFinite(lng) || lng < -180 || lng > 180) throw new Error(`Invalid longitude: ${lng}`);
  if (lat === 0 && lng === 0) throw new Error('Coordinates (0,0) — likely invalid (Null Island)');
}

/**
 * EL4 — non-throwing coordinate validity guard.
 *
 * Used by the elevation providers (and any other provider where
 * "garbage coordinates in → garbage data out" is the failure mode) to
 * short-circuit BEFORE the API call. Throwing is the wrong move for
 * best-effort providers — the caller has no useful recovery beyond
 * "treat the result as unavailable", which is exactly what `null`
 * already means. Returning a structured `{ ok, reason }` lets us emit
 * a `[provider:elevation]` telemetry breadcrumb tagged with the
 * specific rejection cause (null-island, out-of-range, non-finite).
 *
 * Reasons:
 *   - 'non-finite'    — NaN / Infinity / -Infinity (uninitialised state)
 *   - 'out-of-range'  — |lat|>90 or |lng|>180 (data corruption)
 *   - 'null-island'   — (0, 0) — Atlantic point off Africa's coast; in
 *     practice always a sentinel for "no coordinate captured yet".
 */
export interface CoordValidity {
  ok: boolean;
  reason?: 'non-finite' | 'out-of-range' | 'null-island';
}

const SERVER_PROXY_KEY_CONFIG: Record<string, { param: string; env: string }> = {
  'maps.googleapis.com': { param: 'key', env: 'VITE_GOOGLE_MAPS_API_KEY' },
  'developer.nrel.gov': { param: 'api_key', env: 'NREL_API_KEY' },
  'api.eia.gov': { param: 'api_key', env: 'EIA_API_KEY' },
  'www.airnowapi.org': { param: 'API_KEY', env: 'AIRNOW_API_KEY' },
  'api.regulations.gov': { param: 'api_key', env: 'REGULATIONS_GOV_API_KEY' },
  'eutils.ncbi.nlm.nih.gov': { param: 'api_key', env: 'NCBI_API_KEY' },
};

function appendServerApiKey(url: string): string {
  try {
    const parsed = new URL(url);
    const cfg = SERVER_PROXY_KEY_CONFIG[parsed.hostname];
    if (!cfg) return url;
    if (parsed.searchParams.has(cfg.param)) return parsed.toString();
    const key = process.env[cfg.env];
    if (!key) return parsed.toString();
    parsed.searchParams.set(cfg.param, key);
    return parsed.toString();
  } catch {
    return url;
  }
}

export function checkCoordValidity(lat: number, lng: number): CoordValidity {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return { ok: false, reason: 'non-finite' };
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return { ok: false, reason: 'out-of-range' };
  if (lat === 0 && lng === 0) return { ok: false, reason: 'null-island' };
  return { ok: true };
}

/**
 * Wrap a fetch with timeout. Routes through the server-side proxy to avoid
 * CORS issues with external government and data APIs.
 *
 * If the caller has a personal API key set for the upstream's provider,
 * attaches an `x-da-user-key` header so the proxy uses that key instead of
 * the org-level Secret Manager key. The lookup is keyed on the URL's
 * hostname via the registry; unmanaged hosts get no header and behaviour
 * is exactly as before.
 *
 * PH-0.3.2 — `/api/proxy` now requires a Firebase bearer token. In the
 * browser we attach the current user's ID token. Node CLI flows (curator,
 * audit scripts) hit upstream APIs directly, NOT through this helper, so
 * the auth-required gate is safe.
 */
export function fetchWithTimeout(
  url: string,
  timeoutMs: number = 15000,
  signal?: AbortSignal,
): Promise<Response> {
  const controller = new AbortController();
  if (signal?.aborted) controller.abort(signal.reason);
  const onAbort = () => controller.abort(signal?.reason);
  signal?.addEventListener('abort', onAbort, { once: true });
  const timer = setTimeout(() => controller.abort(new Error(`Provider request timed out after ${timeoutMs}ms`)), timeoutMs);
  const isServerRuntime = typeof window === 'undefined' && !process.env.VITEST;
  const targetUrl = isServerRuntime
    ? appendServerApiKey(url)
    : `/api/proxy?url=${encodeURIComponent(url)}`;

  const headers: Record<string, string> = {};
  // No store-registered getter (Node scripts, isolated tests) → returns {} →
  // header builder yields null → behaviour is identical to pre-personal-key.
  const headerValue = buildUserKeyHeader(url, getCurrentUserApiKeys());
  if (headerValue) headers['x-da-user-key'] = headerValue;

  // PH-0.3.2 — /api/proxy is now Firebase-gated. authedFetch attaches the
  // current user's bearer token; in Node CLI / scripts it no-ops, in which
  // case the server returns 401 (Node CLI calls upstream APIs directly, not
  // through this helper, so the gate is safe).
  return Promise.resolve().then(() => {
    const nrcsRequest = isServerRuntime ? prepareNrcsSdaRequest(targetUrl) : null;
    return nrcsRequest
      ? fetchNrcsSdaRequest(nrcsRequest, controller.signal)
      : authedFetch(targetUrl, { signal: controller.signal, headers });
  }).finally(() => {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  });
}
